import { formatReportLabel } from 'containers/Project/utils';

export interface ProjectComparisonReportInfo {
  projectId: number;
  reportId: string;
  name: string;
  products: reports.VersionedProductInfo[];
}

type CustomNameRow = { productId: number; productName: string };

const toReportInfo = (
  projectId: number,
  reportJob: reports.ReportJob,
  customNames: CustomNameRow[],
  nameSuffix = '',
): ProjectComparisonReportInfo => ({
  projectId,
  reportId: reportJob.reportId,
  name:
    formatReportLabel(
      reportJob.projectName,
      reportJob.targetGroupName,
      reportJob.reportType,
      reportJob.startedAt,
    ) + nameSuffix,
  products: (reportJob.reportSummaries?.nodes ?? []).map((summary) => {
    const product = summary.productByProductId;
    const customName = customNames.find((f) => f.productId === product.id);
    return {
      id: product.id,
      canonicalName: product.name,
      name: customName ? customName.productName : product.name,
      version: summary.version,
    };
  }),
});

/**
 * Approved reports of the compared projects. During a QA preview the
 * unapproved report under review is added to the preview project so it can
 * be compared just as the client will be able to once it is approved.
 */
export const buildComparisonReports = (
  data: reports.MultiProjectReport,
  preview?: {
    projectId: number;
    reportJob: reports.ReportJob | undefined;
    nameSuffix: string;
  },
): ProjectComparisonReportInfo[] => {
  const approved = data.projects.nodes.flatMap((project) =>
    project.projectReportsByProjectId.nodes
      .filter((f) => f.reportJob.passedQa)
      .map((item) =>
        toReportInfo(
          project.id,
          item.reportJob,
          project.customPqProductNamesByProjectId.nodes,
        ),
      ),
  );
  const previewJob = preview?.reportJob;
  if (!previewJob || approved.some((r) => r.reportId === previewJob.reportId)) {
    return approved;
  }
  const previewProject = data.projects.nodes.find(
    (p) => p.id === preview.projectId,
  );
  return approved.concat(
    toReportInfo(
      preview.projectId,
      previewJob,
      previewProject?.customPqProductNamesByProjectId.nodes ?? [],
      preview.nameSuffix,
    ),
  );
};

/**
 * Adds the QA preview project to the project picker when it is not one of the
 * active workspace's projects (standalone preview, or a project in the
 * report's own workspace).
 */
export const withPreviewProject = (
  projects: Array<{ id: number; name: string }>,
  previewProjectId: number | undefined,
  previewName: string,
): Array<{ id: number; name: string }> =>
  previewProjectId === undefined ||
  projects.some((p) => p.id === previewProjectId)
    ? projects
    : [{ id: previewProjectId, name: previewName }, ...projects];
